import { useState, useEffect } from "react";
import {
  listConversations,
  createNewConversation,
  listMemories,
  deleteMemory,
  updateMemoryText,
  listActiveGoals,
  setConversationTitle,
  archiveConversation,
  unarchiveConversation,
  getNotificationPrefs,
  upsertNotificationPrefs,
} from "../lib/theoService";
import { registerTheoDevice, sendTheoTestBrief } from "../lib/onesignal";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startYday  = startToday - 24 * 3600 * 1000;
  if (d.getTime() >= startToday) return "Today";
  if (d.getTime() >= startYday)  return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const groupByDay = (convs) => {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  convs.forEach(c => {
    const label = fmtDate(c.updated_at);
    if (label === "Today") groups.Today.push(c);
    else if (label === "Yesterday") groups.Yesterday.push(c);
    else groups.Earlier.push(c);
  });
  return groups;
};

/**
 * Theo Sidebar — slide-in panel with conversation list + memory panel.
 *
 * Props:
 *   userId, open, activeConversationId
 *   onClose()
 *   onNewChat()          — creates a fresh conversation
 *   onSelect(conv)       — switches to an existing conversation
 *   refreshNonce         — bumps to force a reload (e.g. after sending a message)
 */
export default function TheoSidebar({
  userId, open, activeConversationId,
  onClose, onNewChat, onSelect, refreshNonce = 0,
}) {
  const [tab, setTab] = useState("chats"); // "chats" | "memory"
  const [convs, setConvs] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [memories, setMemories] = useState([]);
  const [goals, setGoals] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [menuFor, setMenuFor] = useState(null); // conv id whose 3-dot menu is open
  const [prefs, setPrefs] = useState(null);
  const [prefsSavedAt, setPrefsSavedAt] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState(null); // {kind:'ok'|'err', msg}

  // Load whenever the sidebar opens or after a new send
  useEffect(() => {
    if (!open || !userId) return;
    listConversations(userId).then(setConvs);
    listConversations(userId, { includeArchived: true }).then(all => {
      setArchived(all.filter(c => c.archived));
    });
    listMemories(userId).then(setMemories);
    listActiveGoals(userId).then(setGoals);
    getNotificationPrefs(userId).then(p => {
      if (p && !p._setupRequired) setPrefs(p);
      else if (p && p._setupRequired) setPrefs({ _setupRequired: true });
      else setPrefs({ push_enabled: false, send_hour_local: 8, daily_brief: true, weekly_recap: true });
    });
  }, [open, userId, refreshNonce]);

  if (!open) return null;

  const groups = groupByDay(convs);
  const lastMemoryAt = memories.length > 0 ? memories[0].created_at : null;

  return (
    <>
      {/* Scrim */}
      <div
        data-testid="theo-sidebar-scrim"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 999, animation: "scrimIn 0.2s ease-out both",
        }}
      />
      {/* Panel */}
      <aside
        data-testid="theo-sidebar"
        style={{
          position: "fixed", top: 0, left: 0, bottom: 0,
          width: "min(86vw, 360px)",
          background: "#FFFFFF",
          boxShadow: "8px 0 32px rgba(15,23,42,0.18)",
          zIndex: 1000,
          display: "flex", flexDirection: "column",
          animation: "slideIn 0.28s cubic-bezier(0.22,1,0.36,1) both",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 18px 10px", borderBottom: "1px solid #F1F5F9" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.018em" }}>
              Theo
            </h3>
            <button
              data-testid="theo-sidebar-close"
              onClick={onClose}
              style={{ background: "none", border: "none", fontSize: 20, color: "#94A3B8", cursor: "pointer", lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {/* New chat */}
          <button
            data-testid="theo-new-chat-btn"
            onClick={onNewChat}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #A78BFA, #7C3AED)",
              color: "#fff", border: "none", borderRadius: 12,
              padding: "10px 14px", fontSize: 13.5, fontWeight: 600,
              cursor: "pointer", letterSpacing: "-0.005em",
              boxShadow: "0 4px 12px rgba(124,58,237,0.22)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> New Chat
          </button>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginTop: 14, background: "#F1F5F9", padding: 3, borderRadius: 10 }}>
            <button
              data-testid="theo-tab-chats"
              onClick={() => setTab("chats")}
              style={{
                flex: 1, padding: "6px 10px", fontSize: 12, fontWeight: 600,
                background: tab === "chats" ? "#fff" : "transparent",
                color: tab === "chats" ? "#0F172A" : "#64748B",
                border: "none", borderRadius: 8, cursor: "pointer",
                boxShadow: tab === "chats" ? "0 1px 2px rgba(15,23,42,0.06)" : "none",
              }}
            >
              Chats
            </button>
            <button
              data-testid="theo-tab-memory"
              onClick={() => setTab("memory")}
              style={{
                flex: 1, padding: "6px 10px", fontSize: 12, fontWeight: 600,
                background: tab === "memory" ? "#fff" : "transparent",
                color: tab === "memory" ? "#0F172A" : "#64748B",
                border: "none", borderRadius: 8, cursor: "pointer",
                boxShadow: tab === "memory" ? "0 1px 2px rgba(15,23,42,0.06)" : "none",
              }}
            >
              Things Theo Remembers
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 12px 24px" }}>
          {tab === "chats" && (
            <>
              {convs.length === 0 && (
                <p style={{ fontSize: 12.5, color: "#94A3B8", padding: "20px 8px", textAlign: "center" }}>
                  No conversations yet. Start one with Theo above.
                </p>
              )}
              {["Today", "Yesterday", "Earlier"].map(label => groups[label].length > 0 && (
                <div key={label} style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.14em", padding: "6px 6px 4px" }}>
                    {label.toUpperCase()}
                  </div>
                  {groups[label].map(c => (
                    <div key={c.id} style={{ position: "relative", marginBottom: 4 }}>
                      {renamingId === c.id ? (
                        <div style={{ display: "flex", gap: 4, padding: "6px 8px", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10 }}>
                          <input
                            data-testid={`theo-conv-rename-input-${c.id}`}
                            value={renameText}
                            onChange={(e) => setRenameText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") {
                              const t = renameText.trim().slice(0, 80);
                              if (t) setConversationTitle(c.id, t);
                              setConvs(convs.map(x => x.id === c.id ? { ...x, title: t || x.title } : x));
                              setRenamingId(null); setRenameText("");
                            } else if (e.key === "Escape") { setRenamingId(null); setRenameText(""); } }}
                            autoFocus
                            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#0F172A" }}
                          />
                          <button
                            data-testid={`theo-conv-rename-save-${c.id}`}
                            onClick={async () => {
                              const t = renameText.trim().slice(0, 80);
                              if (t) {
                                await setConversationTitle(c.id, t);
                                setConvs(convs.map(x => x.id === c.id ? { ...x, title: t } : x));
                              }
                              setRenamingId(null); setRenameText("");
                            }}
                            style={{ background: "#7C3AED", color: "#fff", border: "none", borderRadius: 6, padding: "2px 8px", fontSize: 10.5, cursor: "pointer", fontWeight: 600 }}
                          >Save</button>
                        </div>
                      ) : (
                        <>
                          <button
                            data-testid={`theo-conv-item-${c.id}`}
                            onClick={() => onSelect(c)}
                            style={{
                              display: "block", width: "100%", textAlign: "left",
                              padding: "10px 36px 10px 12px",
                              background: c.id === activeConversationId ? "#F5F3FF" : "transparent",
                              border: c.id === activeConversationId ? "1px solid #DDD6FE" : "1px solid transparent",
                              borderRadius: 10, cursor: "pointer",
                              transition: "background 0.12s ease",
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", letterSpacing: "-0.005em", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {c.title}
                            </div>
                            <div style={{ fontSize: 11, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {c.msg_count} msgs · {fmtDate(c.updated_at)}
                            </div>
                          </button>
                          <button
                            data-testid={`theo-conv-menu-${c.id}`}
                            onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id); }}
                            aria-label="Conversation menu"
                            style={{
                              position: "absolute", right: 6, top: 8,
                              background: "transparent", border: "none", color: "#94A3B8",
                              fontSize: 18, lineHeight: 1, padding: "2px 6px",
                              cursor: "pointer", borderRadius: 6,
                            }}
                          >⋯</button>
                          {menuFor === c.id && (
                            <div
                              style={{
                                position: "absolute", right: 6, top: 32, zIndex: 5,
                                background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
                                boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                                padding: 4, minWidth: 130,
                              }}
                              onMouseLeave={() => setMenuFor(null)}
                            >
                              <button
                                data-testid={`theo-conv-rename-${c.id}`}
                                onClick={() => { setRenamingId(c.id); setRenameText(c.title); setMenuFor(null); }}
                                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: "none", fontSize: 12.5, color: "#0F172A", cursor: "pointer", borderRadius: 6 }}
                              >Rename</button>
                              <button
                                data-testid={`theo-conv-archive-${c.id}`}
                                onClick={async () => {
                                  const res = await archiveConversation(c.id);
                                  if (res && res.ok) {
                                    setConvs(convs.filter(x => x.id !== c.id));
                                    setArchived([{ ...c, archived: true }, ...archived]);
                                  } else if (res && res.needsMigration) {
                                    window.alert("Archiving needs a one-time Supabase migration. Open the Memory panel banner to copy the Phase 4A SQL.");
                                  }
                                  setMenuFor(null);
                                }}
                                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: "none", fontSize: 12.5, color: "#B45309", cursor: "pointer", borderRadius: 6 }}
                              >Archive</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Archived section — collapsed by default */}
              {archived.length > 0 && (
                <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed #E2E8F0" }}>
                  <button
                    data-testid="theo-archived-toggle"
                    onClick={() => setShowArchived(o => !o)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "transparent", border: "none",
                      fontSize: 10.5, fontWeight: 800, color: "#94A3B8",
                      letterSpacing: "0.14em", padding: "6px 6px",
                      cursor: "pointer", textTransform: "uppercase",
                    }}
                  >
                    <span style={{ transition: "transform 0.2s", display: "inline-block", transform: showArchived ? "rotate(90deg)" : "none" }}>›</span>
                    Archived ({archived.length})
                  </button>
                  {showArchived && archived.map(c => (
                    <div key={c.id} style={{ position: "relative", marginBottom: 4 }}>
                      <button
                        data-testid={`theo-archived-item-${c.id}`}
                        onClick={() => onSelect(c)}
                        style={{
                          display: "block", width: "100%", textAlign: "left",
                          padding: "8px 36px 8px 12px",
                          background: "transparent", border: "1px solid #F1F5F9",
                          borderRadius: 10, cursor: "pointer", opacity: 0.7,
                        }}
                      >
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: "#475569", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.title}
                        </div>
                        <div style={{ fontSize: 10.5, color: "#94A3B8" }}>
                          {c.msg_count} msgs · {fmtDate(c.updated_at)}
                        </div>
                      </button>
                      <button
                        data-testid={`theo-restore-${c.id}`}
                        onClick={async () => {
                          const res = await unarchiveConversation(c.id);
                          if (res && res.ok) {
                            setArchived(archived.filter(x => x.id !== c.id));
                            setConvs([{ ...c, archived: false }, ...convs]);
                          }
                        }}
                        style={{
                          position: "absolute", right: 6, top: 8,
                          background: "transparent", border: "1px solid #DDD6FE",
                          color: "#7C3AED", fontSize: 10, fontWeight: 600,
                          padding: "3px 8px", borderRadius: 6, cursor: "pointer",
                        }}
                      >Restore</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "memory" && (
            <div data-testid="theo-memory-panel">
              {/* Notifications toggle (Phase 4A infra) */}
              <div style={{ marginTop: 8, padding: "12px 12px", background: "#FAFAF9", border: "1px solid #F1F5F9", borderRadius: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>
                      Daily check-in from Theo
                    </div>
                    <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.4 }}>
                      One short push per day · at {prefs?.send_hour_local ?? 8}:00 your local time
                    </div>
                  </div>
                  <button
                    data-testid="theo-push-toggle"
                    disabled={!prefs || prefs._setupRequired || pushBusy}
                    onClick={async () => {
                      if (!prefs || prefs._setupRequired || pushBusy) return;
                      setPushBusy(true);
                      setPushStatus(null);
                      const wantOn = !prefs.push_enabled;
                      // Optimistic UI flip
                      setPrefs({ ...prefs, push_enabled: wantOn });

                      if (wantOn) {
                        // Turn ON: subscribe via OneSignal + register player with Theo backend
                        const reg = await registerTheoDevice(userId, { sendHourLocal: prefs.send_hour_local ?? 8 });
                        if (!reg.ok) {
                          // Revert toggle on failure
                          setPrefs({ ...prefs, push_enabled: false });
                          await upsertNotificationPrefs(userId, { ...prefs, push_enabled: false });
                          setPushStatus({ kind: "err", msg: reg.reason === "permission_denied"
                            ? "Push permission was denied — enable it in browser settings to receive Theo's daily check-ins."
                            : `Couldn't register device (${reg.reason}). Try again in a moment.` });
                        } else {
                          // Backend has updated prefs; reflect locally
                          setPrefs({ ...prefs, push_enabled: true });
                          setPrefsSavedAt(Date.now());
                          setPushStatus({ kind: "ok", msg: "Subscribed! Theo will check in daily." });
                        }
                      } else {
                        // Turn OFF: just save the pref (we leave the OneSignal subscription intact)
                        const ok = await upsertNotificationPrefs(userId, { ...prefs, push_enabled: false });
                        if (ok) {
                          setPrefsSavedAt(Date.now());
                          setPushStatus({ kind: "ok", msg: "Daily check-ins paused." });
                        }
                      }
                      setPushBusy(false);
                    }}
                    aria-label={prefs?.push_enabled ? "Disable notifications" : "Enable notifications"}
                    style={{
                      width: 38, height: 22, borderRadius: 999,
                      background: prefs?.push_enabled ? "#7C3AED" : "#CBD5E1",
                      border: "none", cursor: prefs?._setupRequired || pushBusy ? "not-allowed" : "pointer",
                      position: "relative", transition: "background 0.2s ease",
                      flexShrink: 0,
                      opacity: prefs?._setupRequired ? 0.4 : (pushBusy ? 0.6 : 1),
                    }}
                  >
                    <span style={{
                      position: "absolute", top: 2, left: prefs?.push_enabled ? 18 : 2,
                      width: 18, height: 18, borderRadius: "50%",
                      background: "#fff", transition: "left 0.2s ease",
                      boxShadow: "0 1px 2px rgba(15,23,42,0.18)",
                    }} />
                  </button>
                </div>
                {prefs?._setupRequired && (
                  <div style={{ fontSize: 10.5, color: "#92400E", marginTop: 8 }}>
                    Run the Phase 4A schema migration to enable notification preferences.
                  </div>
                )}
                {pushStatus && (
                  <div
                    data-testid="theo-push-status"
                    style={{
                      fontSize: 10.8, marginTop: 8, lineHeight: 1.4,
                      color: pushStatus.kind === "ok" ? "#047857" : "#B91C1C",
                    }}
                  >
                    {pushStatus.msg}
                  </div>
                )}
                {prefs?.push_enabled && !prefs._setupRequired && (
                  <button
                    data-testid="theo-send-test-brief-btn"
                    disabled={pushBusy}
                    onClick={async () => {
                      setPushBusy(true);
                      setPushStatus({ kind: "ok", msg: "Sending a test brief…" });
                      const res = await sendTheoTestBrief(userId);
                      const sent = (res.results || []).some(r => r.status === "sent");
                      setPushStatus(sent
                        ? { kind: "ok", msg: "Test brief sent — check your notifications in a moment." }
                        : { kind: "err", msg: `Test send issue: ${(res.results || [])[0]?.reason || res.reason || "no response"}` });
                      setPushBusy(false);
                    }}
                    style={{
                      marginTop: 10, padding: "6px 12px", fontSize: 11.5, fontWeight: 600,
                      background: "transparent", color: "#7C3AED",
                      border: "1px solid rgba(124, 58, 237, 0.35)",
                      borderRadius: 999, cursor: pushBusy ? "not-allowed" : "pointer",
                      opacity: pushBusy ? 0.6 : 1,
                    }}
                  >
                    Send me a test brief now
                  </button>
                )}
                {prefsSavedAt && (
                  <div style={{ fontSize: 10.5, color: "#059669", marginTop: 6 }}>✓ Saved</div>
                )}
              </div>

              {/* Goals */}
              {goals.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.14em", padding: "6px 6px 6px" }}>
                    ACTIVE GOALS
                  </div>
                  {goals.map(g => (
                    <div key={g.id} style={{ padding: "8px 10px", background: "#F5F3FF", borderRadius: 10, marginBottom: 6, fontSize: 12.5, color: "#0F172A" }}>
                      {g.goal_text}
                    </div>
                  ))}
                </div>
              )}

              {/* Memories */}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 6px 6px" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.14em" }}>
                    MEMORIES ({memories.length})
                  </span>
                  {lastMemoryAt && (
                    <span style={{ fontSize: 9.5, color: "#94A3B8" }}>
                      updated {fmtDate(lastMemoryAt)}
                    </span>
                  )}
                </div>
                {memories.length === 0 && (
                  <p style={{ fontSize: 12, color: "#94A3B8", padding: "12px 8px", textAlign: "center" }}>
                    Theo hasn&apos;t remembered anything yet. Tell him something to remember.
                  </p>
                )}
                {memories.map(m => (
                  <div
                    key={m.id}
                    data-testid={`memory-item-${m.id}`}
                    style={{ padding: "10px 12px", background: "#FAFAF9", border: "1px solid #F1F5F9", borderRadius: 10, marginBottom: 6 }}
                  >
                    {editingId === m.id ? (
                      <>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          style={{ width: "100%", border: "1px solid #DDD6FE", borderRadius: 8, padding: 8, fontSize: 12.5, minHeight: 56, resize: "vertical", fontFamily: "inherit" }}
                        />
                        <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                          <button
                            onClick={() => { setEditingId(null); setEditText(""); }}
                            style={{ background: "transparent", border: "1px solid #E2E8F0", borderRadius: 8, padding: "4px 10px", fontSize: 11, color: "#64748B", cursor: "pointer" }}
                          >
                            Cancel
                          </button>
                          <button
                            data-testid={`memory-save-${m.id}`}
                            onClick={async () => {
                              const ok = await updateMemoryText(m.id, editText);
                              if (ok) {
                                const fresh = await listMemories(userId);
                                setMemories(fresh);
                              }
                              setEditingId(null); setEditText("");
                            }}
                            style={{ background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
                          >
                            Save
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 12.5, color: "#0F172A", lineHeight: 1.5, marginBottom: 4 }}>
                          {m.memory_text}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 10.5, color: "#94A3B8" }}>
                            {fmtDate(m.created_at)}
                          </span>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              data-testid={`memory-edit-${m.id}`}
                              onClick={() => { setEditingId(m.id); setEditText(m.memory_text); }}
                              style={{ background: "transparent", border: "none", color: "#7C3AED", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0 }}
                            >
                              Edit
                            </button>
                            <button
                              data-testid={`memory-delete-${m.id}`}
                              onClick={async () => {
                                if (!window.confirm("Delete this memory? Theo will forget it.")) return;
                                const ok = await deleteMemory(m.id);
                                if (ok) setMemories(memories.filter(x => x.id !== m.id));
                              }}
                              style={{ background: "transparent", border: "none", color: "#DC2626", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0 }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
          @keyframes scrimIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </aside>
    </>
  );
}
